import dayjs from '../utils/dayjs';
import moment from 'moment-timezone';

export const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const reinterpretDateInTimeZone = (
  value: Date | number | string,
  timeZone: string,
) => {
  return dayjs(value).tz(timeZone, true);
};

export class TimezoneUtil {
  static getTimezoneList() {
    if (!('supportedValuesOf' in Intl)) {
      return [{ label: 'UTC', value: 'UTC' }];
    } else {
      const continents = [
        'Africa',
        'America',
        'Antarctica',
        'Asia',
        'Atlantic',
        'Australia',
        'Europe',
        'Indian',
        'Pacific',
      ];
      const locale = navigator.language;
      const timeZoneList = [{ label: 'UTC', value: 'UTC' }];
      for (const timezone of Intl.supportedValuesOf('timeZone')) {
        const continent = timezone.split('/');
        if (continent.length <= 1) {
          continue;
        }
        if (continents.indexOf(continent[0]) === -1) {
          continue;
        }
        const offset = TimezoneUtil.getUTCOffset(locale, timezone);
        timeZoneList.push({
          label: timezone + ' ' + offset,
          value: timezone,
        });
      }
      return timeZoneList;
    }
  }

  static readonly convertDateToUtcEpoch = (date: Date, timeZone: string) => {
    return dayjs(date).tz(timeZone, true).endOf('day').utc().valueOf();
  };

  static readonly getUTCOffset = (locale: string, timeZone: string) => {
    return TimezoneUtil.getFormattedElement(
      locale,
      timeZone,
      'timeZoneName',
      'longOffset',
    );
  };

  static readonly getFormattedElement = (
    locale: string,
    timeZone: string,
    name: string,
    value: string,
  ): string | undefined => {
    const options: Intl.DateTimeFormatOptions = { timeZone };
    (options[name as keyof Intl.DateTimeFormatOptions] as string | undefined) =
      value;
    const parts = new Intl.DateTimeFormat(locale, options)
      .formatToParts(new Date())
      .find((el) => el.type === name);
    return parts?.value;
  };
}

export const timezoneOptions = TimezoneUtil.getTimezoneList();

export const getOrdinal = (day: number) => {
  const suffix = ['th', 'st', 'nd', 'rd'];
  const modulo10 = day % 10;
  return suffix[(modulo10 - 1) % 10] || suffix[0];
};

export function getTimeZoneAbbreviation(date: Date, timeZone?: string) {
  const abbr = moment.tz(date, timeZone ?? currentTimeZone).format('z');

  // If it's a numeric offset like "+12", "-3", etc., convert to GMT+/- format
  if (/^[+-]?\d+$/.test(abbr)) {
    return `GMT${abbr.startsWith('-') || abbr.startsWith('+') ? abbr : '+' + abbr}`;
  }

  // Otherwise return the abbreviation as-is (e.g., IST, EST)
  return abbr;
}

export const getExpirationDate = (date?: Date, timeZone?: string) => {
  if (!date) {
    return '-';
  }

  const zonedDate = dayjs(date).tz(timeZone ?? currentTimeZone);

  const month = zonedDate.format('MMM.');
  const day = zonedDate.date();
  const year = zonedDate.year();

  return `${month} ${day}${getOrdinal(day)} ${year} ${getTimeZoneAbbreviation(
    date,
    timeZone,
  )}`;
};

export const timeZoneToUtc = (
  expirationDate: Date,
  expirationTimeZone: string,
) => {
  return TimezoneUtil.convertDateToUtcEpoch(expirationDate, expirationTimeZone);
};

export const getFilterDate = (date: Date) => {
  const isToday = dayjs(date).isSame(dayjs(), 'day');

  return isToday ? Date.now() : dayjs(date).endOf('day').valueOf();
};

export const isSameDateOnly = (
  a?: Date | number | null,
  b?: Date | number | null,
) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  return dayjs(a).isSame(dayjs(b), 'day');
};

export const isSameDate = (
  dateA: Date,
  dateB: Date | string | number,
  timeZone = currentTimeZone,
): boolean => {
  try {
    const zonedDateB = dayjs(dateB).tz(timeZone);

    return (
      dateA.getFullYear() === zonedDateB.year() &&
      dateA.getMonth() === zonedDateB.month() &&
      dateA.getDate() === zonedDateB.date()
    );
  } catch {
    return false;
  }
};

export const convertTimestampToTimezone = (
  timestamp?: number | null,
  timeZone?: string,
) => {
  if (!timestamp) return null;

  return reinterpretDateInTimeZone(
    timestamp,
    timeZone ?? currentTimeZone,
  ).valueOf();
};
